import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Estadisticas = () => {
  const [activeTab, setActiveTab] = useState("planning");
  const [stats, setStats] = useState(null);
  const [dossierStats, setDossierStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDossiers, setLoadingDossiers] = useState(false);
  const [viewMode, setViewMode] = useState("year");
  const [dossierViewMode, setDossierViewMode] = useState("month");
  const [filterFuente, setFilterFuente] = useState("");
  const [filterVivienda, setFilterVivienda] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-12-31`;
  });
  const [dossierStartDate, setDossierStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-01-01`;
  });
  const [dossierEndDate, setDossierEndDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-12-31`;
  });
  const [showDossierTable, setShowDossierTable] = useState(false);

  const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/appointments/stats?start_date=${startDate}&end_date=${endDate}`;
      if (filterFuente) url += `&fuente=${encodeURIComponent(filterFuente)}`;
      if (filterVivienda) url += `&vivienda=${encodeURIComponent(filterVivienda)}`;
      const res = await axios.get(url);
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, filterFuente, filterVivienda]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fetchDossierStats = useCallback(async () => {
    setLoadingDossiers(true);
    try {
      const res = await axios.get(`${API}/dossiers/stats?start_date=${dossierStartDate}&end_date=${dossierEndDate}&view=${dossierViewMode}`);
      setDossierStats(res.data);
    } catch (err) {
      console.error("Error fetching dossier stats:", err);
    } finally {
      setLoadingDossiers(false);
    }
  }, [dossierStartDate, dossierEndDate, dossierViewMode]);

  useEffect(() => { if (activeTab === "dossiers") fetchDossierStats(); }, [activeTab, fetchDossierStats]);

  const setDossierQuickRange = (type) => {
    const now = new Date();
    if (type === "year") {
      setDossierStartDate(`${now.getFullYear()}-01-01`);
      setDossierEndDate(`${now.getFullYear()}-12-31`);
    } else if (type === "month") {
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      setDossierStartDate(`${now.getFullYear()}-${m}-01`);
      setDossierEndDate(`${now.getFullYear()}-${m}-${last}`);
    } else if (type === "week") {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(now);
      monday.setDate(diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setDossierStartDate(fmt(monday));
      setDossierEndDate(fmt(sunday));
    }
  };

  const setQuickRange = (type) => {
    const now = new Date();
    if (type === "year") {
      setStartDate(`${now.getFullYear()}-01-01`);
      setEndDate(`${now.getFullYear()}-12-31`);
    } else if (type === "month") {
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      setStartDate(`${now.getFullYear()}-${m}-01`);
      setEndDate(`${now.getFullYear()}-${m}-${last}`);
    } else if (type === "week") {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(now);
      monday.setDate(diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setStartDate(fmt(monday));
      setEndDate(fmt(sunday));
    }
  };

  const maxBarVal = (obj) => Math.max(...Object.values(obj || {}), 1);

  const StatCard = ({ title, data, colorFn, keys }) => {
    const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((a, [, b]) => a + b, 0);
    const max = Math.max(...entries.map(([, v]) => v), 1);
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">{title}</p>
        {entries.length === 0
          ? <p className="text-xs text-slate-400 italic">Sin datos</p>
          : <div className="space-y-2.5">
              {entries.map(([key, val]) => (
                <div key={key}>
                  <div className="flex justify-between items-center text-xs mb-1">
                    <span className="text-slate-700 font-medium truncate mr-2">{key}</span>
                    <span className="text-slate-500 shrink-0">{val} · <span className="text-slate-400">{total ? Math.round(val/total*100) : 0}%</span></span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(val/max)*100}%`, backgroundColor: colorFn ? colorFn(key, keys) : '#3b82f6' }} />
                  </div>
                </div>
              ))}
            </div>
        }
      </div>
    );
  };

  // Dynamic color palette — auto-assigned by position, no hardcoded names
  const PALETTE = ["#3b82f6","#8b5cf6","#f59e0b","#22c55e","#ef4444","#06b6d4","#f97316","#ec4899","#14b8a6","#a855f7"];
  const dynColor = (list, key) => { const i = list.indexOf(key); return i >= 0 ? PALETTE[i % PALETTE.length] : "#94a3b8"; };
  const statusColors = { "Instalación programada": "#f59e0b", "Instalado": "#22c55e", "Validado": "#15803d", "Anulado": "#ef4444" };

  const breakdownTitle = viewMode === "week" ? "Desglose semanal" : viewMode === "month" ? "Desglose mensual" : "Desglose anual";
  const breakdownData = viewMode === "week" ? (stats?.by_week || {}) : viewMode === "month" ? (stats?.by_month || {}) : (stats?.by_year || {});

  const formatBreakdownLabel = (key) => {
    if (viewMode === "year") return key;
    if (viewMode === "month") {
      const [y, m] = key.split("-");
      return `${monthNames[parseInt(m, 10) - 1]} ${y}`;
    }
    return key;
  };

  const allSources = stats ? Object.keys(stats.by_source || {}).filter(s => s !== "Sin fuente") : [];
  const allTypes = stats ? Object.keys(stats.by_type || {}).filter(t => t !== "Sin tipo") : [];

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="estadisticas-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Estadísticas</h1>
        <p className="text-sm text-slate-500 mt-1">Rendimiento por vendedor, fuente, estado y vivienda</p>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-100 rounded-lg p-0.5 w-fit">
        <button
          onClick={() => setActiveTab("planning")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === "planning" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-planning-stats"
        >
          Planning
        </button>
        <button
          onClick={() => setActiveTab("dossiers")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === "dossiers" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-dossiers-stats"
        >
          Dossiers
        </button>
      </div>

      {activeTab === "planning" ? (
      <>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          {[["week", "Semana"], ["month", "Mes"], ["year", "Año"]].map(([key, label]) => (
            <button key={key} onClick={() => { setViewMode(key); setQuickRange(key); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`stats-view-${key}`}>{label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 w-36 text-xs" data-testid="stats-start-date" />
          <span className="text-slate-400 text-xs">a</span>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 w-36 text-xs" data-testid="stats-end-date" />
        </div>
        {stats && (
          <div className="ml-auto flex items-center gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
              <div className="text-[10px] text-slate-400 uppercase">Total citas</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{stats.total_m2 || 0}</div>
              <div className="text-[10px] text-slate-400 uppercase">m² superficie</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.total_m2_install || 0}</div>
              <div className="text-[10px] text-slate-400 uppercase">m² instalación</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterFuente || "_all"} onValueChange={v => setFilterFuente(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-48 text-xs" data-testid="stats-filter-fuente">
            <SelectValue placeholder="Todas las fuentes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todas las fuentes</SelectItem>
            {allSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            {allSources.length === 0 && ["Google ADS", "Facebook & Instagram", "Tik Tok ADS", "Parrainage", "Autre"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterVivienda || "_all"} onValueChange={v => setFilterVivienda(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-48 text-xs" data-testid="stats-filter-vivienda">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todos los tipos</SelectItem>
            {allTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            {allTypes.length === 0 && ["apartamento", "casa", "local"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filterFuente || filterVivienda) && (
          <button onClick={() => { setFilterFuente(""); setFilterVivienda(""); }} className="text-xs text-red-500 hover:text-red-700 underline" data-testid="stats-clear-filters">
            Limpiar filtros
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : stats && (
        <>
          {/* KPI résumé */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Total citas", value: stats.total, color: "text-slate-800" },
              { label: "m² superficie", value: stats.total_m2 || 0, color: "text-emerald-600" },
              { label: "m² instalación", value: stats.total_m2_install || 0, color: "text-blue-600" },
            ].map(k => (
              <div key={k.label} className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-center">
                <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{k.label}</p>
              </div>
            ))}
          </div>

          {/* 4 cartes stats dynamiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {(() => {
              const vendeurKeys = Object.keys(stats.by_vendeur || {}).sort((a,b) => (stats.by_vendeur[b]||0)-(stats.by_vendeur[a]||0));
              const sourceKeys  = Object.keys(stats.by_source  || {}).sort((a,b) => (stats.by_source[b] ||0)-(stats.by_source[a] ||0));
              return <>
                <StatCard title="Vendedor" data={stats.by_vendeur} colorFn={(k) => dynColor(vendeurKeys, k)} keys={vendeurKeys} />
                <StatCard title="Fuente"   data={stats.by_source}  colorFn={(k) => dynColor(sourceKeys,  k)} keys={sourceKeys}  />
                <StatCard title="Estado"   data={stats.by_status}  colorFn={(k) => statusColors[k] || '#94a3b8'} />
                <StatCard title="Vivienda" data={stats.by_type}    colorFn={(k, keys) => dynColor(keys || [], k)} />
              </>;
            })()}
          </div>
        </>
      )}
      </>
      ) : (
      /* ===== DOSSIERS TAB ===== */
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {[["day", "Día"], ["week", "Semana"], ["month", "Mes"], ["year", "Año"]].map(([key, label]) => (
              <button key={key} onClick={() => { setDossierViewMode(key); setDossierQuickRange(key); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${dossierViewMode === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`dossier-view-${key}`}>{label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-2">
            <Input type="date" value={dossierStartDate} onChange={e => setDossierStartDate(e.target.value)} className="h-8 w-36 text-xs" data-testid="dossier-start-date" />
            <span className="text-slate-400 text-xs">a</span>
            <Input type="date" value={dossierEndDate} onChange={e => setDossierEndDate(e.target.value)} className="h-8 w-36 text-xs" data-testid="dossier-end-date" />
          </div>
          {dossierStats && (
            <div className="ml-auto flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-800">{dossierStats.total_clients}</div>
                <div className="text-[10px] text-slate-400 uppercase">Total clients</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">{dossierStats.total_m2}</div>
                <div className="text-[10px] text-slate-400 uppercase">m² isolés</div>
              </div>
            </div>
          )}
        </div>

        {loadingDossiers ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : dossierStats && Object.keys(dossierStats.by_period).length > 0 ? (
          <Card data-testid="dossier-chart-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">m² isolés y Clientes por período</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const entries = Object.entries(dossierStats.by_period);
                const maxM2 = Math.max(...entries.map(([, d]) => d.m2), 1);
                const maxClients = Math.max(...entries.map(([, d]) => d.clients), 1);
                const [showTable, setShowTable] = [showDossierTable, setShowDossierTable];
                const [selectedPeriod, setSelectedPeriod] = [showTable, setShowTable];
                return (
                  <div className="space-y-3">
                    {/* Chart */}
                    <div className="flex items-end gap-1 overflow-x-auto pb-2" style={{ minHeight: 180 }}>
                      {entries.map(([key, data]) => (
                        <div
                          key={key}
                          className={`flex flex-col items-center gap-0.5 cursor-pointer rounded-t px-0.5 transition-colors ${selectedPeriod === key ? "bg-slate-100" : "hover:bg-slate-50"}`}
                          style={{ minWidth: 32, flex: 1, maxWidth: 60 }}
                          onClick={() => setSelectedPeriod(selectedPeriod === key ? null : key)}
                        >
                          <div className="flex items-end gap-0.5" style={{ height: 130 }}>
                            <div
                              className="bg-emerald-500 rounded-t-sm transition-all hover:bg-emerald-600"
                              style={{ width: 14, height: Math.max(4, (data.m2 / maxM2) * 120) }}
                            />
                            <div
                              className="bg-blue-500 rounded-t-sm transition-all hover:bg-blue-600"
                              style={{ width: 14, height: Math.max(4, (data.clients / maxClients) * 120) }}
                            />
                          </div>
                          <div className="text-[8px] text-slate-400 text-center leading-tight whitespace-nowrap truncate w-full">
                            {dossierViewMode === "month" ? key.split("-")[1] : key.length > 6 ? key.slice(0, 5) : key}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Legend */}
                    <div className="flex items-center gap-4 justify-center text-xs">
                      <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> m² isolés</div>
                      <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Clients</div>
                    </div>
                    {/* Selected period detail */}
                    {selectedPeriod && dossierStats.by_period[selectedPeriod] && (
                      <div className="bg-slate-50 rounded-lg p-3 text-center animate-fade-in-up">
                        <p className="text-xs text-slate-500 mb-1">{dossierViewMode === "month" ? selectedPeriod.split("-").reverse().join("/") : selectedPeriod}</p>
                        <div className="flex items-center justify-center gap-6">
                          <div>
                            <span className="text-lg font-bold text-emerald-600">{dossierStats.by_period[selectedPeriod].m2}</span>
                            <span className="text-xs text-slate-400 ml-1">m²</span>
                          </div>
                          <div>
                            <span className="text-lg font-bold text-blue-600">{dossierStats.by_period[selectedPeriod].clients}</span>
                            <span className="text-xs text-slate-400 ml-1">clients</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex items-center justify-center py-16">
              <p className="text-slate-400 text-sm">No hay dossiers para este período</p>
            </CardContent>
          </Card>
        )}
      </>
      )}
    </div>
  );
};

export default Estadisticas;
